/**
 * Laporan P5 — valuasi persediaan, aging piutang, penjualan periode, export.
 *
 * ⛔ TIAP BAGIAN MENAMPILKAN `dasar` DARI BACKEND apa adanya — dua layar yang
 *    menjelaskan basis angka dengan kalimatnya sendiri akan berselisih, dan
 *    yang membacanya tidak tahu mana yang benar.
 *
 * ⛔ BAGIAN YANG 403 DISEMBUNYIKAN, BUKAN DIRUSAK: peran yang boleh membuka
 *    halaman ini belum tentu boleh semua bagiannya (ADMIN_GUDANG hanya
 *    valuasi; CS hanya aging). Gerbangnya backend; layar cuma tidak
 *    memajang pintu yang pasti terkunci.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { ANALITIK_ROLES, KPI_ROLES } from "@/lib/roles";
import { tanggalLokalISO } from "../lib/format";
import Hint from "@/components/etams/Hint";

const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR",
                                   maximumFractionDigits: 0 }).format(n || 0);

const KERANJANG_LABEL = {
  BELUM_JATUH_TEMPO: "Belum jatuh tempo",
  "1_30": "1–30 hari",
  "31_60": "31–60 hari",
  "61_90": "61–90 hari",
  LEBIH_90: "> 90 hari",
};

function Bagian({ judul, anak, aksi }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-medium text-foreground">{judul}</p>
        {aksi}
      </div>
      {anak}
    </div>
  );
}

// Inventaris "layar Laporan tanpa tanda memuat" (MANDAT-TUNTAS-25SEP): bagian yang datanya belum datang dulu KOSONG —
// tak terbedakan dari "tidak ada data". Kini "Memuat…", atau "gagal dimuat" bila ada galat.
function Memuat({ galat }) {
  return (
    <div className="text-sm text-muted-foreground" data-testid="laporan-memuat">
      {galat ? "Gagal dimuat — lihat pesan di atas." : "Memuat…"}
    </div>
  );
}

export default function Laporan() {
  const { profile } = useAuth();
  const role = profile?.role?.code;
  const [galat, setGalat] = useState("");

  const [valuasi, setValuasi] = useState(null);
  const [aging, setAging] = useState(null);
  const [dari, setDari] = useState(() => tanggalLokalISO().slice(0, 8) + "01");
  const [sampai, setSampai] = useState(() => tanggalLokalISO());
  const [jual, setJual] = useState(null);

  const bolehValuasi = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG", "FINANCE"].includes(role);
  const bolehAging = ["OWNER", "KEPALA_CABANG", "CS", "FINANCE"].includes(role);
  const bolehJual = ["OWNER", "KEPALA_CABANG", "FINANCE"].includes(role);
  const bolehEkspor = ["OWNER", "FINANCE"].includes(role);

  useEffect(() => {
    if (bolehValuasi)
      api.get("/laporan/valuasi-persediaan")
        .then(({ data }) => setValuasi(data)).catch((e) => setGalat(apiErrorMessage(e)));
    if (bolehAging)
      api.get("/laporan/aging-piutang")
        .then(({ data }) => setAging(data)).catch((e) => setGalat(apiErrorMessage(e)));
  }, [bolehValuasi, bolehAging]);

  const muatJual = useCallback(() => {
    if (!bolehJual) return;
    api.get("/laporan/penjualan", { params: { dari, sampai } })
      .then(({ data }) => setJual(data)).catch((e) => setGalat(apiErrorMessage(e)));
  }, [bolehJual, dari, sampai]);
  useEffect(() => { muatJual(); }, [muatJual]);

  const unduh = useCallback(async (jenis) => {
    // Unduhan lewat blob supaya header Authorization ikut — tautan polos
    // tidak membawa token dan akan 401.
    try {
      const r = await api.get(`/laporan/ekspor/${jenis}`, {
        params: { dari, sampai }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etams_${jenis}_${dari}_${sampai}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setGalat(apiErrorMessage(e)); }
  }, [dari, sampai]);

  return (
    <div className="max-w-5xl space-y-6" data-testid="laporan-page">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold text-foreground">
            Laporan{" "}
            <Hint>Semua angka dihitung sistem dari data yang tercatat, tidak dihitung ulang di layar.</Hint>
          </h1>
          {KPI_ROLES.includes(role) && (
            <Link to="/laporan/kpi" className="text-sm underline" data-testid="laporan-ke-kpi">KPI ketepatan waktu &amp; Pareto komplain</Link>
          )}
          {ANALITIK_ROLES.includes(role) && (
            <Link to="/laporan/analitik" className="text-sm underline" data-testid="laporan-ke-analitik">Analitik (waste kain, lead time vendor, repeat musiman, profitabilitas)</Link>
          )}
        </div>
      </div>

      {galat && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{galat}</div>
      )}

      {bolehJual && (
        <Bagian judul="Penjualan per periode"
          aksi={
            <div className="flex items-center gap-2">
              <input type="date" value={dari} onChange={(e) => setDari(e.target.value)}
                     className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground" />
              <span className="text-xs text-muted-foreground">s/d</span>
              <input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)}
                     className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground" />
            </div>}
          anak={!jual ? <Memuat galat={galat} /> : (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[["FA lahir", jual.total_fa_lahir, false],
                  ["Nilai FA (dijanjikan)", jual.total_nilai_fa_idr, true],
                  ["Uang masuk terkonfirmasi", jual.total_uang_masuk_idr, true]].map(([l, v, uang]) => (
                  <div key={l} className="rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">{l}</p>
                    <p className="text-sm font-semibold tabular-nums text-foreground">{uang ? rupiah(v) : v}</p>
                  </div>
                ))}
              </div>
              {Object.keys(jual.per_store).length > 0 && (
                <table className="w-full text-sm">
                  <thead className="border-b text-xs text-muted-foreground">
                    <tr><th className="py-1 text-left">Cabang</th><th className="text-right">FA lahir</th><th className="text-right">Nilai FA</th><th className="text-right">Uang masuk</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(jual.per_store).map(([nama, d]) => (
                      <tr key={nama} className="border-b border-border">
                        <td className="py-1">{nama}</td>
                        <td className="text-right tabular-nums">{d.fa_lahir}</td>
                        <td className="text-right tabular-nums">{rupiah(d.nilai_fa_idr)}</td>
                        <td className="text-right tabular-nums">{rupiah(d.uang_masuk_idr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-xs text-muted-foreground">{jual.dasar}</p>
            </>
          )} />
      )}

      {bolehAging && (
        <Bagian judul="Aging piutang customer"
          anak={!aging ? <Memuat galat={galat} /> : (
            <>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(aging.per_keranjang).map(([k, v]) => (
                  <div key={k}
                       className={`rounded-lg border p-3 ${k === "LEBIH_90" && v.jumlah > 0
                         ? "border-danger-soft-border bg-danger-soft" : "border-border"}`}>
                    <p className="text-xs text-muted-foreground">{KERANJANG_LABEL[k]}</p>
                    <p className="text-sm font-semibold tabular-nums text-foreground">{rupiah(v.nominal_idr)}</p>
                    <p className="text-xs text-muted-foreground">{v.jumlah} piutang</p>
                  </div>
                ))}
              </div>
              {aging.rinci.length === 0 && (
                <p className="text-xs text-muted-foreground">Tidak ada piutang terbuka.</p>
              )}
            </>
          )} />
      )}

      {bolehValuasi && (
        <Bagian judul="Valuasi persediaan"
          anak={!valuasi ? <Memuat galat={galat} /> : (
            <>
              {valuasi.temuan?.length > 0 && (
                <div className="rounded border border-warning-soft-border bg-warning-soft p-2 text-xs text-warning-soft-foreground">
                  {valuasi.temuan.map((t, i) => <p key={i}>⚠ {t}</p>)}
                </div>
              )}
              <p className="text-lg font-semibold tabular-nums text-foreground">{rupiah(valuasi.total_idr)}</p>
              {valuasi.baris.length > 0 && (
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-xs text-muted-foreground">
                      <tr><th className="py-1 text-left">Bahan</th><th className="text-left">Cabang</th><th className="text-right">Saldo</th><th className="text-right">Harga rata</th><th className="text-right">Nilai</th></tr>
                    </thead>
                    <tbody>
                      {valuasi.baris.map((b, i) => (
                        <tr key={i} className="border-b border-border">
                          <td className="py-1">{b.material_name}</td>
                          <td className="text-xs text-muted-foreground">{b.store_name}</td>
                          <td className="text-right tabular-nums">{b.balance}</td>
                          <td className="text-right tabular-nums">{rupiah(b.harga_rata_idr)}</td>
                          <td className="text-right tabular-nums">{rupiah(b.nilai_idr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-muted-foreground">{valuasi.dasar}</p>
            </>
          )} />
      )}

      {bolehEkspor && (
        <Bagian judul={<>Unduh CSV{" "}<Hint>Kolom: tanggal, nomor dokumen, lawan transaksi, nominal, jenis — bisa dibuka di Excel. Periode mengikuti tanggal di bagian Penjualan.</Hint></>}
          anak={
            <>
              <div className="flex gap-2">
                {["penjualan", "pembelian", "upah", "refund"].map((j) => (
                  <button key={j} type="button" onClick={() => unduh(j)}
                          className="rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                    Unduh {j}
                  </button>
                ))}
              </div>
            </>
          } />
      )}
    </div>
  );
}
